/*
 * <product-card-variant-picker>
 *
 * Enhances the server-rendered markup in snippets/product-card-variant-picker.liquid.
 * One listbox per multi-value product option. On change it:
 *   1. resolves the variant from the current option combination
 *   2. swaps the card image, reusing the theme's `theme:swatch:change` handler on the
 *      ancestor <product-grid-item-variant> (assets/theme.js, ProductGridItemVariant)
 *   3. retargets the hidden `id` input on the add to cart form
 *   4. enables/disables the button and relabels it Sold out / Add to cart
 *
 * Each listbox opens as a top-layer popover and is positioned from the toggle's viewport
 * rect, so the horizontal card tracks on mobile cannot clip it. Browsers without popover
 * keep the absolutely positioned list.
 *
 * Price is deliberately left alone — see the snippet's doc comment.
 */
if (!customElements.get('product-card-variant-picker')) {
  customElements.define(
    'product-card-variant-picker',
    class ProductCardVariantPicker extends HTMLElement {
      connectedCallback() {
        this.variants = this.parseVariants();
        this.optionGroups = Array.from(this.querySelectorAll('[data-ref="option"]'));
        this.submit = this.querySelector('[data-ref="submit"]');
        this.submitLabel = this.querySelector('[data-ref="submit-label"]');
        this.idInput = this.querySelector('input[name="id"]');

        // Without popover the list stays absolutely positioned inside the card, and stays
        // clipped by any scroll container — the pre-existing behaviour.
        this.supportsPopover = typeof HTMLElement.prototype.showPopover === 'function';
        this.openGroupEl = null;
        this.repositionBound = false;

        this.onReposition = () => {
          if (this.openGroupEl) this.positionList(this.openGroupEl);
        };

        // No dropdowns (single-variant product, or every option has one value) — the
        // static add to cart button is already correct, so there is nothing to wire up.
        if (!this.variants.length || !this.optionGroups.length || !this.idInput) return;

        const current =
          this.variants.find((variant) => String(variant.id) === String(this.idInput.value)) ||
          this.variants[0];

        this.selections = current.options.slice();

        this.onDocumentClick = (event) => {
          this.optionGroups.forEach((group) => {
            if (!group.contains(event.target)) this.closeGroup(group);
          });
        };

        this.onDocumentKeydown = (event) => {
          if (event.key !== 'Escape') return;
          const open = this.optionGroups.find((group) => this.isOpen(group));
          if (!open) return;
          this.closeGroup(open);
          this.toggleOf(open).focus();
        };

        document.addEventListener('click', this.onDocumentClick);
        document.addEventListener('keydown', this.onDocumentKeydown);

        /*
         * Once per instance, not once per connection. connectedCallback runs again every
         * time the element is re-parented — which the collection page does when AJAX
         * filtering or pagination swaps the grid, and the theme editor does on re-render.
         * Listeners survive a move, so re-binding would give each toggle two click
         * handlers and the second would immediately undo the first, leaving the dropdown
         * unopenable. Everything above is safe to recompute; this is not.
         */
        if (!this.groupsBound) {
          this.groupsBound = true;
          this.optionGroups.forEach((group) => this.bindGroup(group));
        }

        this.refreshAvailability();
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('keydown', this.onDocumentKeydown);
        this.unbindReposition();
      }

      /* ---------- setup ---------- */

      parseVariants() {
        const script = this.querySelector('[data-ref="variants"]');
        if (!script) return [];

        try {
          const parsed = JSON.parse(script.textContent);
          return Array.isArray(parsed) ? parsed : [];
        } catch (error) {
          console.error('product-card-variant-picker: could not parse variant data', error);
          return [];
        }
      }

      bindGroup(group) {
        const toggle = this.toggleOf(group);

        toggle.addEventListener('click', () => {
          if (this.isOpen(group)) {
            this.closeGroup(group);
          } else {
            this.openGroup(group);
          }
        });

        toggle.addEventListener('keydown', (event) => {
          if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
          event.preventDefault();
          this.openGroup(group);
          const items = this.itemsOf(group);
          const target = event.key === 'ArrowDown' ? items[0] : items[items.length - 1];
          if (target) target.focus();
        });

        this.itemsOf(group).forEach((item) => {
          item.addEventListener('click', () => {
            this.select(group, item.dataset.value);
            this.closeGroup(group);
            toggle.focus();
          });

          item.addEventListener('keydown', (event) => {
            const items = this.itemsOf(group);
            const index = items.indexOf(item);

            switch (event.key) {
              case 'Enter':
              case ' ':
                event.preventDefault();
                this.select(group, item.dataset.value);
                this.closeGroup(group);
                toggle.focus();
                break;
              case 'ArrowDown':
                event.preventDefault();
                (items[index + 1] || items[0]).focus();
                break;
              case 'ArrowUp':
                event.preventDefault();
                (items[index - 1] || items[items.length - 1]).focus();
                break;
              case 'Home':
                event.preventDefault();
                items[0].focus();
                break;
              case 'End':
                event.preventDefault();
                items[items.length - 1].focus();
                break;
              case 'Tab':
                this.closeGroup(group);
                break;
            }
          });
        });
      }

      /* ---------- open / close ---------- */

      toggleOf(group) {
        return group.querySelector('[data-ref="toggle"]');
      }

      listOf(group) {
        return group.querySelector('[data-ref="list"]');
      }

      itemsOf(group) {
        return Array.from(group.querySelectorAll('[data-ref="item"]'));
      }

      isOpen(group) {
        return this.toggleOf(group).getAttribute('aria-expanded') === 'true';
      }

      openGroup(group) {
        this.optionGroups.forEach((other) => {
          if (other !== group) this.closeGroup(other);
        });

        const list = this.listOf(group);

        // `hidden` still wins over :popover-open, so it has to come off either way.
        list.hidden = false;

        if (this.supportsPopover) {
          if (!list.matches(':popover-open')) list.showPopover();
          this.openGroupEl = group;
          this.positionList(group);
          this.bindReposition();
        }

        this.toggleOf(group).setAttribute('aria-expanded', 'true');
      }

      closeGroup(group) {
        const list = this.listOf(group);

        if (this.supportsPopover) {
          if (list.matches(':popover-open')) list.hidePopover();
          list.style.removeProperty('top');
          list.style.removeProperty('left');
          list.style.removeProperty('width');

          if (this.openGroupEl === group) {
            this.openGroupEl = null;
            this.unbindReposition();
          }
        }

        list.hidden = true;
        this.toggleOf(group).setAttribute('aria-expanded', 'false');
      }

      /*
       * A top-layer popover is positioned against the viewport, so it has to be placed
       * from the toggle's rect and kept in step with anything that moves the toggle —
       * including the horizontal card track it lives in.
       */
      positionList(group) {
        const toggle = this.toggleOf(group);
        const list = this.listOf(group);
        const rect = toggle.getBoundingClientRect();

        list.style.width = `${rect.width}px`;
        list.style.left = `${rect.left}px`;

        // Measured after the width is applied, since the width decides how many option
        // labels wrap, and so the height.
        const height = list.offsetHeight;
        const spaceBelow = window.innerHeight - rect.bottom;
        const flip = spaceBelow < height && rect.top > spaceBelow;

        // The half-pixel collapses the shared border with the toggle, matching the
        // fallback's `top: calc(100% - 0.5px)`.
        list.style.top = flip ? `${rect.top - height + 0.5}px` : `${rect.bottom - 0.5}px`;
      }

      // Capture phase, so scrolling any ancestor scroller counts — not just the page.
      bindReposition() {
        if (this.repositionBound) return;
        this.repositionBound = true;
        window.addEventListener('scroll', this.onReposition, { capture: true, passive: true });
        window.addEventListener('resize', this.onReposition);
      }

      unbindReposition() {
        if (!this.repositionBound) return;
        this.repositionBound = false;
        window.removeEventListener('scroll', this.onReposition, { capture: true });
        window.removeEventListener('resize', this.onReposition);
      }

      /* ---------- selection ---------- */

      findVariant(options) {
        return this.variants.find((variant) =>
          variant.options.every((option, index) => option === options[index])
        );
      }

      select(group, value) {
        const optionIndex = Number(group.dataset.optionIndex);
        const candidate = this.selections.slice();
        candidate[optionIndex] = value;

        // Prefer the exact combination. Shopify does not always have a variant for every
        // permutation, so fall back to the first variant carrying this value and let the
        // other dropdowns follow it.
        const variant =
          this.findVariant(candidate) ||
          this.variants.find((entry) => entry.options[optionIndex] === value);

        if (!variant) return;

        this.selections = variant.options.slice();
        this.syncLabels();
        this.refreshAvailability();
        this.applyVariant(variant);
      }

      syncLabels() {
        this.optionGroups.forEach((group) => {
          const optionIndex = Number(group.dataset.optionIndex);
          const selected = this.selections[optionIndex];

          this.itemsOf(group).forEach((item) => {
            item.setAttribute('aria-selected', String(item.dataset.value === selected));
          });

          // ARTIST, 2026-09-25 : l'étiquette courte du format (« 50 ml ») quand l'extrait
          // format-court en a préparé une; la vraie valeur reste dans data-value.
          const valueEl = group.querySelector('[data-ref="value"]');
          const chosenItem = this.itemsOf(group).find((item) => item.dataset.value === selected);
          if (valueEl) valueEl.textContent = (chosenItem && chosenItem.dataset.label) || selected;

          // Mirror the chosen value's swatch into the toggle.
          const slot = group.querySelector('[data-ref="swatch-slot"]');
          if (!slot) return;

          const chosen = this.itemsOf(group).find((item) => item.dataset.value === selected);
          const swatch = chosen && chosen.querySelector('.product-card-variant-picker__swatch');
          slot.replaceChildren(...(swatch ? [swatch.cloneNode(true)] : []));
        });
      }

      refreshAvailability() {
        this.optionGroups.forEach((group) => {
          const optionIndex = Number(group.dataset.optionIndex);

          this.itemsOf(group).forEach((item) => {
            const value = item.dataset.value;

            // Available if some in-stock variant carries this value here while matching
            // the shopper's current choices for every other option.
            const available = this.variants.some(
              (variant) =>
                variant.available &&
                variant.options[optionIndex] === value &&
                variant.options.every(
                  (option, index) => index === optionIndex || option === this.selections[index]
                )
            );

            // Still selectable — picking it should surface the Sold out button.
            item.toggleAttribute('data-unavailable', !available);
          });
        });
      }

      applyVariant(variant) {
        this.idInput.value = variant.id;
        this.updateSubmit(variant);
        if (variant.media) this.showMedia(variant.media);
      }

      updateSubmit(variant) {
        if (!this.submit) return;

        this.submit.disabled = !variant.available;

        if (this.submitLabel) {
          this.submitLabel.textContent = variant.available
            ? this.dataset.addToCartText
            : this.dataset.soldOutText;
        }
      }

      /* ---------- card image ---------- */

      showMedia(mediaId) {
        const card = this.closest('product-grid-item');
        if (!card) return;

        const gridVariant =
          card.querySelector('product-grid-item-variant:not([hidden])') ||
          card.querySelector('product-grid-item-variant');
        if (!gridVariant) return;

        const target = gridVariant.querySelector(`[data-grid-image-target="${mediaId}"]`);
        if (!target) return;

        // Unwraps the <template> the theme uses to defer off-screen images.
        target.setAttribute('loading', 'eager');

        gridVariant.dispatchEvent(
          new CustomEvent('theme:swatch:change', {
            detail: { id: mediaId, stopSlideAnimation: true },
          })
        );

        // ProductGridItemVariant only listens when the card has more than one image and
        // the viewport is not mobile, so finish the job ourselves if nothing happened.
        if (!target.hasAttribute('data-grid-current-image')) {
          gridVariant
            .querySelectorAll('[data-grid-image][data-grid-current-image]')
            .forEach((image) => image.removeAttribute('data-grid-current-image'));
          target.setAttribute('data-grid-current-image', 'true');
        }
      }
    }
  );
}
